import React, { FC, Fragment } from "react";
import {
    CloseIconButton,
    DescriptionSubTypography,
    DescriptionTypography,
    ImagePreviewItem,
    PollOptionsPreviewContainer,
    PollOptionsPreviewContainerStack,
    PollOptionsPreviewStack,
    PollOptionsRatePreviewStack,
    PreviewBlockImageContainer,
} from "./PostForm.styles";
import { CloseIcon } from "@icons/CloseIcon";
import { SquarePollSolidIcon } from "@icons/SquarePollSolidIcon";
import { Stack } from "@mui/material";
import { CircleIcon } from "@icons/CircleLeft";
import { usePollOptionsState } from "./PollTab/PollOptionsState";
import dayjs from "dayjs";
import { PollTypesEnum } from "../../../Shared/Types/Polls";
import { SliderSolidIcon } from "@icons/SliderIcon";
import { CircleCheckSolidIcon } from "@icons/CircleCheckSolidIcon";
import { usePostFormPollMediaFilesUploadInputStore } from "./PollTab/PollsTabSections/PostFormPollMediaFilesUploadInput.store";

export function PostPollOptionsPreview({
    handleDeleteSelectedPollOptions,
    onContainerClick,
}: {
    handleDeleteSelectedPollOptions: (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => void;
    onContainerClick: () => void;
}) {
    const { closedAt, pollType } = usePollOptionsState();

    return (
        <PollOptionsPreviewContainer>
            <PollOptionsPreviewContainerStack
                onClick={() => {
                    onContainerClick();
                }}
            >
                {pollType === PollTypesEnum.POLL_RATING ? (
                    <RatePreview />
                ) : pollType === PollTypesEnum.POLL_IMAGES ? (
                    <ImagePreview />
                ) : (
                    <TextOptionsPreview />
                )}
                <CloseIconButton
                    onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSelectedPollOptions(e);
                    }}
                >
                    <CloseIcon />
                </CloseIconButton>
            </PollOptionsPreviewContainerStack>
            <ExpirationDate closedAt={closedAt} />
        </PollOptionsPreviewContainer>
    );
}

const RatePreview: FC = () => {
    return (
        <PollOptionsRatePreviewStack>
            <PreviewBlockImageContainer>
                <SliderSolidIcon width="20px" height="20px" />
            </PreviewBlockImageContainer>
            <DescriptionTypography>Slider</DescriptionTypography>
        </PollOptionsRatePreviewStack>
    );
};

const TextOptionsPreview: FC = () => {
    const { getFilteredPollOptions } = usePollOptionsState();
    const pollOptions = getFilteredPollOptions();
    return (
        <>
            <PreviewBlockImageContainer>
                <SquarePollSolidIcon width="20px" height="20px" />
            </PreviewBlockImageContainer>
            <PollOptionsPreviewStack>
                <DescriptionTypography>
                    {pollOptions.length + " Antwortoptionen"}
                </DescriptionTypography>
                {pollOptions.map(({ content, isCorrectAnswer }, key) => (
                    <Stack
                        direction={"row"}
                        gap={"4px"}
                        alignItems={"center"}
                        marginTop={"4px"}
                        key={key}
                    >
                        {isCorrectAnswer ? (
                            <CircleCheckSolidIcon
                                width="12px"
                                height="12px"
                                color="var(--community-color-light-steal-blue)"
                            />
                        ) : (
                            <CircleIcon
                                width="12px"
                                height="12px"
                                color="var(--community-color-secondary-text)"
                            />
                        )}
                        <DescriptionSubTypography>
                            {content}
                        </DescriptionSubTypography>
                    </Stack>
                ))}
            </PollOptionsPreviewStack>
        </>
    );
};

const ImagePreview: FC = () => {
    const { images } = usePostFormPollMediaFilesUploadInputStore();
    return (
        <>
            <PreviewBlockImageContainer>
                <SquarePollSolidIcon width="20px" height="20px" />
            </PreviewBlockImageContainer>
            <PollOptionsPreviewStack>
                <DescriptionTypography>
                    {images.length + " Antwortoptionen"}
                </DescriptionTypography>
                <Stack
                    direction={"row"}
                    gap={"4px"}
                    alignItems={"center"}
                    marginTop={"4px"}
                >
                    {images
                        .filter(
                            (image) => !image.isInappropriate && !image.delete,
                        )
                        .map(({ imageUrl }, key) => (
                            <Fragment key={key}>
                                <ImagePreviewItem image={imageUrl} />
                            </Fragment>
                        ))}
                </Stack>
            </PollOptionsPreviewStack>
        </>
    );
};

const ExpirationDate: FC<{ closedAt?: number }> = ({ closedAt }) => {
    const expirationDate = closedAt ? dayjs().add(closedAt, "h") : undefined;
    if (!expirationDate) {
        return null;
    }
    return (
        <DescriptionSubTypography marginTop="8px">
            {`Umfrage läuft bis ${expirationDate.format(
                "DD.MM.YYYY",
            )} um ${expirationDate.format("HH:mm")} Uhr.`}
        </DescriptionSubTypography>
    );
};
